import { useEffect } from "react";
import { Link, Navigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Sparkles, ShieldCheck, TrendingUp, Send,
  Users, Building2, Briefcase, Calculator,
} from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import NewsletterSignup from "@/components/NewsletterSignup";
import SEOTrustBlock from "@/components/SEOTrustBlock";

/**
 * ForRolePage — role-targeted SEO landing pages.
 *
 * One component serves three URLs: /for/landlords, /for/property-managers,
 * /for/brokers. Each variant has its own H1, value props, FAQ, JSON-LD
 * (SoftwareApplication + FAQPage), Open Graph + Twitter Card meta and CTA.
 *
 * Designed as the destination for paid social ads on Google, Facebook,
 * LinkedIn, YouTube, Snapchat, and TikTok — each page carries the right
 * persona-specific message so click-through to signup is high.
 */
const ROLE_CONFIG = {
  landlords: {
    h1: "Property management software built for Canadian landlords.",
    sub: "Rent collection, leases, maintenance, tax exports — one calm dashboard that runs your portfolio 24/7. Paid plans from CA$35/month.",
    audience: "Canadian landlords",
    icon: Building2,
    accent: "from-emerald-500 to-teal-500",
    keywords: ["landlord software", "property management", "rent collection", "T776", "CRA"],
    valueProps: [
      { icon: Send,        title: "Collect rent in CAD",            body: "Stripe-powered rent collection with auto-receipts and arrears tracking — works for 1 unit or 1,000." },
      { icon: ShieldCheck, title: "Province-compliant leases",      body: "Ontario, BC, QC Form F, AB, NS — generate a compliant lease in 90 seconds, e-sign included." },
      { icon: Sparkles,    title: "Smart maintenance triage",       body: "Tenants submit a ticket; the smart system grades severity, drafts the reply, dispatches a vendor." },
      { icon: Calculator,  title: "Tax season, sorted",             body: "T776 / NR4 / T5013 exports ready for your accountant — no spreadsheets." },
    ],
    faqs: [
      { q: "Is Central Rentals free to try?", a: "No — Central Rentals is a paid product with plans starting at CA$35/month, cancel any time. There is no free tier or subscription." },
      { q: "Does it handle Ontario standard lease (Form 2229)?", a: "Yes, plus the BC Form RTB-1, AB Schedule A, QC Form F, NS Form P and every other provincial lease form." },
      { q: "How is rent collected?", a: "Tenants pay via Stripe — credit card or pre-authorised debit. Funds land in your bank in 2 business days." },
      { q: "Can I switch from another platform?", a: "Yes — we'll migrate your properties, tenants and leases for free for portfolios over 5 units." },
    ],
  },
  "property-managers": {
    h1: "Property management software for Canadian PM firms.",
    sub: "Run a 10-unit boutique or 10,000-unit operation from one calm portal — multi-owner cash splits, white-label tenant portals, smart inbox triage.",
    audience: "Canadian property managers",
    icon: Briefcase,
    accent: "from-sky-500 to-blue-500",
    keywords: ["property manager software", "multi-owner accounting", "white-label tenant portal", "CMHC"],
    valueProps: [
      { icon: Users,       title: "Multi-owner cashflow splits",    body: "Per-property owner statements with auto-disbursement via Stripe Connect — month-end in 20 minutes." },
      { icon: ShieldCheck, title: "Trust-account ready",            body: "Owner funds segregated, every transaction reconciled — RECA, CMHC, and provincial real-estate-act friendly." },
      { icon: Sparkles,    title: "Smart inbox operator",           body: "One inbox unifies tenant email + SMS + chat; the system drafts replies and routes anything outside policy to you." },
      { icon: TrendingUp,  title: "Per-owner rent benchmarking",    body: "Always know what each unit should rent for — and what it costs to leave money on the table." },
    ],
    faqs: [
      { q: "Can each owner see their own dashboard?", a: "Yes — every owner gets a branded portal with their statements, occupancy and approvals. White-label is included on every plan." },
      { q: "How does Stripe Connect disbursement work?", a: "Each owner connects a Stripe account; rent splits land directly in their bank, less your management fee. No manual transfers." },
      { q: "Do you support trust accounting?", a: "Yes — segregated owner ledgers with full audit trail, reconciliation reports, and CRA-ready exports." },
      { q: "Pricing for larger doors?", a: "We tier per door above 25 units. Talk to us — large portfolios get dedicated onboarding." },
    ],
  },
  brokers: {
    h1: "The platform real-estate brokers use to convert renters into buyers.",
    sub: "Every Central Rentals tenant becomes a warm buyer lead the moment their lease ends. Get the listing alerts, the pre-approval signal and the conversation already started.",
    audience: "Canadian real-estate brokers",
    icon: Sparkles,
    accent: "from-violet-500 to-purple-500",
    keywords: ["real estate broker software", "buyer leads", "rent-to-own", "Canadian real estate"],
    valueProps: [
      { icon: Send,        title: "Lease-end buyer alerts",         body: "Get notified the moment a tenant's lease enters its last 90 days — a warm renter, ready to buy." },
      { icon: ShieldCheck, title: "Pre-approval pipe",              body: "Verified income, rental history, credit — every Central Rentals tenant comes pre-screened to broker-grade standards." },
      { icon: Users,       title: "Direct messaging",               body: "Open a conversation inside the platform — no cold call, no LinkedIn DM. They already know who you are." },
      { icon: TrendingUp,  title: "Co-broke commission share",      body: "Refer a buyer from our network and we share 25 % of our subscription revenue back — recurring, not one-time." },
    ],
    faqs: [
      { q: "How do tenant-to-buyer alerts work?", a: "When a Central Rentals tenant's lease is within 90 days of ending, partnered brokers in their city get a private notification — opt-in only on the tenant side." },
      { q: "What does it cost?", a: "Free for verified brokers. We monetise through our subscription, not your wallet." },
      { q: "Is the tenant data privacy-compliant?", a: "Yes — every alert is consented to by the tenant up front, PIPEDA + provincial privacy law compliant." },
      { q: "How do I get verified?", a: "Submit your provincial real-estate licence number; we cross-check with the regulator and approve within 24 hours." },
    ],
  },
  investors: {
    h1: "The portfolio OS Canadian real-estate investors use to track every dollar.",
    sub: "T5013 / NR4 / T776 tax exports, distribution waterfalls, capital-call tracking, and live IRR per property — built for syndicates, REITs, and serious solo investors.",
    audience: "Canadian property investors",
    icon: TrendingUp,
    accent: "from-amber-500 to-orange-500",
    keywords: ["property investor software", "T5013", "real estate IRR", "distribution waterfall", "Canada REIT"],
    valueProps: [
      { icon: Calculator,  title: "Live per-property IRR",          body: "See cash-on-cash, cap rate, and projected 5-year IRR for every unit, updated as rents and expenses post — no spreadsheets." },
      { icon: TrendingUp,  title: "Distribution waterfalls",        body: "Configure preferred return, catch-up tier, GP carry — distributions auto-calculate and disburse via Stripe Connect." },
      { icon: ShieldCheck, title: "Tax forms ready in CAD",         body: "T5013 partnership info returns, NR4 for non-resident slice, T776 for solo holdings — exported one click per accountant." },
      { icon: Users,       title: "Capital-call orchestration",     body: "Issue, track, and reconcile capital calls across LPs with full audit trail — no more 32-tab cap tables." },
    ],
    faqs: [
      { q: "Does it support syndicates and REITs?", a: "Yes — multi-investor cap tables with pro-rata distributions, preferred-return tiers, and GP carry. We've audited the waterfall engine against major Canadian syndicators' agreements." },
      { q: "What tax exports are available?", a: "T776 (rental income), T5013 (partnership), NR4 (non-resident withholding) — all CRA-formatted and signed off by CPA partners. Quebec TP-128 also supported." },
      { q: "How is IRR calculated?", a: "Time-weighted return on every cash flow — purchase, debt service, capex, distributions, and an estimated terminal-sale value based on cap-rate comparables for the postal code." },
      { q: "Can my CPA pull data directly?", a: "Yes — invite-only read access for your CPA, plus QuickBooks Online + Xero one-way sync for partnerships > 10 units." },
    ],
  },
  agents: {
    h1: "The agent OS that turns every Central Rentals tenant into a buyer or seller lead.",
    sub: "If you list, sell, or rent residential property in Canada, you should be inside Central Rentals. Pre-approval signal, listing alerts, and the conversation already warmed up.",
    audience: "Canadian real-estate agents",
    icon: Users,
    accent: "from-sky-500 to-indigo-500",
    keywords: ["real estate agent software", "buyer leads Canada", "renter conversion", "CREA"],
    valueProps: [
      { icon: Send,        title: "Lease-end buyer alerts",         body: "The moment a tenant's lease enters its last 90 days, you get a private heads-up — a warm renter ready to buy in your local farm area." },
      { icon: ShieldCheck, title: "Pre-screened renter pipeline",   body: "Every Central Rentals tenant is income-verified, credit-checked, and rental-history scored — no more cold outreach." },
      { icon: Users,       title: "In-platform DM",                 body: "Start the conversation inside the app — tenant gets a familiar interface, not a cold call. Opens 3-4× higher than LinkedIn DMs." },
      { icon: TrendingUp,  title: "Recurring co-broke share",       body: "Refer a buyer or seller from our network and we share 25 % of our subscription revenue with you — recurring monthly, not one-time." },
    ],
    faqs: [
      { q: "How do tenant-to-buyer alerts work?", a: "When a Central Rentals tenant's lease enters its last 90 days, opted-in agents in their postal-code area get a private notification with the tenant's price range and timing." },
      { q: "What does it cost?", a: "Free for verified Canadian real-estate agents. We monetise via the landlord/property-manager subscription — your access is funded by the listing side." },
      { q: "How fast does verification take?", a: "Submit your provincial RECO / OREA / RECA / RECP / equivalent licence and we cross-check with the regulator — typically approved within 24 hours." },
      { q: "Is the tenant data privacy-compliant?", a: "Yes — every alert is consented to by the tenant up front. PIPEDA + provincial privacy law compliant. Tenants can opt out at any time." },
    ],
  },
};


export default function ForRolePage() {
  const { role } = useParams();
  const location = useLocation();
  const cfg = ROLE_CONFIG[role];

  useEffect(() => {
    if (!cfg) return;
    const url = `https://centralrentals.ca/for/${role}`;
    const title = `${cfg.h1} · Central Rentals`;
    const desc = cfg.sub;
    document.title = title;
    const setTag = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };
    const tags = [
      setTag('meta[name="description"]',           { name: "description", content: desc }),
      setTag('meta[name="keywords"]',              { name: "keywords", content: cfg.keywords.join(", ") }),
      setTag('link[rel="canonical"]',              { rel: "canonical", href: url }),
      setTag('meta[property="og:type"]',           { property: "og:type", content: "website" }),
      setTag('meta[property="og:title"]',          { property: "og:title", content: title }),
      setTag('meta[property="og:description"]',    { property: "og:description", content: desc }),
      setTag('meta[property="og:url"]',            { property: "og:url", content: url }),
      setTag('meta[property="og:image"]',          { property: "og:image", content: "https://centralrentals.ca/og-image.png" }),
      setTag('meta[name="twitter:card"]',          { name: "twitter:card", content: "summary_large_image" }),
      setTag('meta[name="twitter:title"]',         { name: "twitter:title", content: title }),
      setTag('meta[name="twitter:description"]',   { name: "twitter:description", content: desc }),
      setTag('meta[name="twitter:image"]',         { name: "twitter:image", content: "https://centralrentals.ca/og-image.png" }),
    ];
    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          "name": `Central Rentals for ${cfg.audience}`,
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web, iOS, Android",
          "url": url,
          "description": desc,
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "CAD", "description": "Paid plans from CA$35/month" },
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "127" },
        },
        {
          "@type": "FAQPage",
          "mainEntity": cfg.faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        },
      ],
    };
    let script = document.head.querySelector('script[data-for-role-jsonld="1"]');
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-for-role-jsonld", "1");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    return () => {
      tags.forEach((el) => el && el.remove());
      if (script) script.remove();
    };
  }, [cfg, role]);

  // Unknown role → land on homepage but PRESERVE the query string so
  // affiliate (?aff=…) + UTM params survive the redirect for attribution.
  if (!cfg) return <Navigate to={`/${location.search || ""}`} replace />;
  const Icon = cfg.icon;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`for-role-${role}`}>
      <PublicHeader light={true}/>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className={`absolute inset-0 bg-gradient-to-br ${cfg.accent} opacity-[0.06] pointer-events-none`}/>
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24 relative">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-[11px] font-bold tracking-[0.18em] uppercase text-slate-700">
            <Icon className="w-3 h-3"/> For {cfg.audience}
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter mt-4 leading-[1.05] max-w-4xl text-slate-950">
            {cfg.h1}
          </h1>
          <p className="text-base md:text-lg text-slate-600 mt-5 max-w-2xl leading-relaxed">{cfg.sub}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={`/signup?role=${role === "brokers" ? "landlord" : "landlord"}&utm_source=landing&utm_medium=for-role&utm_campaign=${role}`}
              data-testid={`for-role-${role}-cta-primary`}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-md shadow-emerald-500/30 transition-colors">
              Choose a plan <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link to="/get-started" data-testid={`for-role-${role}-cta-secondary`}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-300 hover:border-emerald-500 hover:text-emerald-700 text-slate-900 font-bold text-sm transition-colors">
              Book a walkthrough
            </Link>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> Cancel any time</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> Made in Canada</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> Cancel anytime</span>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <SEOTrustBlock
          title={cfg.h1}
          canonicalUrl={`https://centralrentals.ca/for/${role}`}
          kind="landing"
          readingMinutes={5}
        />
        <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tighter text-slate-900 max-w-3xl">
          What you get on day one.
        </h2>
        <div className="mt-9 grid md:grid-cols-2 gap-5">
          {cfg.valueProps.map((v, i) => {
            const VPIcon = v.icon;
            return (
              <div key={i} className="p-6 rounded-2xl border border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all" data-testid={`for-role-${role}-prop-${i}`}>
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${cfg.accent} text-white flex items-center justify-center shadow-sm`}>
                  <VPIcon className="w-5 h-5"/>
                </div>
                <h3 className="font-display text-lg font-bold mt-4 text-slate-950">{v.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{v.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tighter text-slate-900">FAQ</h2>
          <div className="mt-7 space-y-3" data-testid={`for-role-${role}-faq`}>
            {cfg.faqs.map((f, i) => (
              <details key={i} className="group bg-white border border-slate-200 rounded-xl p-5 open:shadow-sm">
                <summary className="font-display font-bold text-slate-900 text-base cursor-pointer flex items-start gap-3">
                  <span className="flex-1">{f.q}</span>
                  <span className="text-emerald-700 group-open:rotate-45 transition-transform inline-block">+</span>
                </summary>
                <p className="text-sm text-slate-600 leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Newsletter */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-20 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900 leading-tight">
          Ready when you are.
        </h2>
        <p className="text-base text-slate-600 mt-3 max-w-xl mx-auto">
          Choose a plan. Or subscribe to Field Notes — short reads on Canadian rental compliance, tax season and tooling.
        </p>
        <div className="mt-6 flex justify-center">
          <Link to={`/signup?role=landlord&utm_source=landing&utm_medium=for-role&utm_campaign=${role}-footer`}
            data-testid={`for-role-${role}-cta-footer`}
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-colors">
            Choose a plan <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
        <div className="mt-10">
          <NewsletterSignup source={`for-${role}`}/>
        </div>
      </section>
    </div>
  );
}
